import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Box, Button, Typography } from '@mui/material';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import landlordSignature from './landlord-signature.png';
import leaseTemplate from './lease-template.pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export default function LeaseSigner() {
  const [numPages, setNumPages] = useState(null);
  const [signed, setSigned] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [signaturePlaced, setSignaturePlaced] = useState(false);
  const [initialPlaced, setInitialPlaced] = useState(false);

  const tenantName = 'Ashley Dotson';
  const leaseAddress = '750 Hilton St Memphis, TN 38114';
  const rent = '$1850.00';
  const startDate = 'December 1, 2024';
  const endDate = 'November 30, 2025';

  const today = new Date();
  const daySuffix = (d) =>
    `${d}${[,'st','nd','rd'][d%10] && ![11,12,13].includes(d) ? [,'st','nd','rd'][d%10] : 'th'}`;
  const dateClause = `${daySuffix(today.getDate())} day of ${today.toLocaleString('default', {
    month: 'long',
  })}, ${today.getFullYear()}`;
  const formattedDate = today.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const preparePdf = async () => {
    const existingPdfBytes = await fetch(leaseTemplate).then((res) => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const pages = pdfDoc.getPages();
    const firstPage = pages[0];
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    firstPage.drawText(tenantName, { x: 140, y: 630, size: 10, font, color: rgb(0, 0, 0) });
    firstPage.drawText(leaseAddress, { x: 140, y: 610, size: 10, font, color: rgb(0, 0, 0) });
    firstPage.drawText(startDate, { x: 140, y: 590, size: 10, font, color: rgb(0, 0, 0) });
    firstPage.drawText(endDate, { x: 140, y: 570, size: 10, font, color: rgb(0, 0, 0) });
    firstPage.drawText(rent, { x: 140, y: 550, size: 10, font, color: rgb(0, 0, 0) });
    firstPage.drawText(dateClause, { x: 200, y: 705, size: 10, font, color: rgb(0, 0, 0) });

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    setPdfBlobUrl(url);
  };

  useEffect(() => {
    preparePdf();
  }, []);

  const handleSign = async () => {
    const existingPdfBytes = await fetch(leaseTemplate).then((res) => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const pages = pdfDoc.getPages();
    const firstPage = pages[0];
    const lastPage = pages[pages.length - 1];
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    lastPage.drawText(tenantName, { x: 130, y: 145, size: 14, font, color: rgb(0, 0, 0) });
    lastPage.drawText(formattedDate, { x: 400, y: 145, size: 10, font, color: rgb(0, 0, 0) });

    lastPage.drawText('Memphis Passive', { x: 130, y: 235, size: 14, font, color: rgb(0, 0, 0) });
    lastPage.drawText(formattedDate, { x: 400, y: 235, size: 10, font, color: rgb(0, 0, 0) });

    firstPage.drawText(tenantName.charAt(0), { x: 490, y: 718, size: 14, font, color: rgb(0, 0, 0) });

    const landlordBytes = await fetch(landlordSignature).then((res) => res.arrayBuffer());
    const landlordPng = await pdfDoc.embedPng(landlordBytes);
    lastPage.drawImage(landlordPng, { x: 130, y: 205, width: 150, height: 30 });

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'Signed_Lease.pdf';
    link.click();
    URL.revokeObjectURL(url);

    setSigned(true);
  };

  return (
    <Box sx={{ p: 4, position: 'relative' }}>
      <Typography variant="h5" gutterBottom>
        Sign Your Lease Agreement
      </Typography>

      {pdfBlobUrl && (
        <Box sx={{ position: 'relative' }}>
          <Document file={pdfBlobUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
            {Array.from(new Array(numPages), (_, index) => (
              <Page key={`page_${index + 1}`} pageNumber={index + 1} scale={1.7} />
            ))}
          </Document>

          {!initialPlaced && (
            <Button
              variant="contained"
              size="small"
              sx={{ position: 'absolute', top: 180, right: 160, zIndex: 10 }}
              onClick={() => setInitialPlaced(true)}
            >
              Initial Here
            </Button>
          )}
          {initialPlaced && (
            <Typography
              sx={{
                position: 'absolute',
                top: 240,
                right: 160,
                fontFamily: 'cursive',
                fontSize: '20px',
                zIndex: 10,
              }}
            >
              {tenantName.charAt(0)}
            </Typography>
          )}

          {!signaturePlaced && (
            <Button
              variant="contained"
              size="small"
              sx={{ position: 'absolute', bottom: 95, left: 130, zIndex: 10 }}
              onClick={() => setSignaturePlaced(true)}
            >
              Sign Here
            </Button>
          )}
          {signaturePlaced && (
            <Typography
              sx={{
                position: 'absolute',
                bottom: 95,
                left: 130,
                fontFamily: 'cursive',
                fontSize: '24px',
                zIndex: 10,
              }}
            >
              {tenantName}
            </Typography>
          )}
        </Box>
      )}

      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 4 }}
        onClick={handleSign}
        disabled={!signaturePlaced || !initialPlaced || signed}
      >
        Submit Lease
      </Button>

      {signed && (
        <Typography sx={{ mt: 2, color: 'green' }}>Lease submitted successfully!</Typography>
      )}
    </Box>
  );
}